import { useEffect, useState } from 'react';
import { authedFetch, type SessionState } from '../lib/session.js';
import { navigate } from '../lib/hash-route.js';

interface Props<T> {
  session: SessionState;
  title: string;
  subtitle?: React.ReactNode;
  fetchPath: string;
  /** Key in the JSON response that contains the array of items. */
  itemsKey: string;
  /** Render one row. Return JSX. Wrap in <li className="row-item"> upstream. */
  renderRow: (item: T) => React.ReactNode;
  /** Optional row-level onClick handler for navigation. */
  onItemClick?: (item: T) => void;
  emptyMessage?: string;
}

/**
 * Generic overlay for drill-down lists. Reused by EmailsList, WhatsAppList,
 * DealsList, MeetingsList, OpportunitiesList. Clicking the backdrop closes.
 */
export function ListOverlay<T extends { id?: string }>({
  session, title, subtitle, fetchPath, itemsKey, renderRow, onItemClick, emptyMessage,
}: Props<T>) {
  const [items, setItems] = useState<T[] | null>(null);
  /** Cuantas hay de verdad, si el servidor lo dice (la lista puede venir cortada). */
  const [total, setTotal] = useState<number | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const r = await authedFetch(session, fetchPath);
        if (!r.ok) {
          const body = await r.text().catch(() => '');
          throw new Error(`${r.status}: ${body.slice(0, 200)}`);
        }
        const j = (await r.json()) as Record<string, unknown>;
        const list = j[itemsKey] as T[] | undefined;
        if (!cancelled) { setItems(list ?? []); setTotal(typeof j.total === 'number' ? j.total : null); }
      } catch (e) {
        if (!cancelled) setErr(e instanceof Error ? e.message : String(e));
      }
    })();
    return () => { cancelled = true; };
  }, [session.token, fetchPath, itemsKey]);

  return (
    <div className="overlay" onClick={() => navigate({ kind: 'snapshot' })}>
      <div className="overlay-card" onClick={(e) => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 16 }}>
          <div>
            <div className="topbar-brand">{title}</div>
            <h2 style={{ margin: '4px 0 0', fontSize: 18, fontWeight: 600 }}>
              {items
                ? total !== null && total > items.length
                  ? `${items.length} de ${total}`
                  : `${items.length} resultado${items.length === 1 ? '' : 's'}`
                : 'Cargando…'}
            </h2>
            {subtitle && <div className="topbar-meta" style={{ marginTop: 4 }}>{subtitle}</div>}
          </div>
          <button className="tab" onClick={() => navigate({ kind: 'snapshot' })}>✕ Cerrar</button>
        </div>

        {err && <div className="section-empty" style={{ color: 'var(--danger)' }}>{err}</div>}
        {!items && !err && <div className="section-empty">Cargando…</div>}
        {items && items.length === 0 && <div className="section-empty">{emptyMessage ?? 'Sin resultados.'}</div>}

        {items && items.length > 0 && (
          <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
            {items.map((it, i) => (
              <li
                key={(it as { id?: string }).id ?? `row-${i}`}
                className="row-item"
                onClick={onItemClick ? () => onItemClick(it) : undefined}
                style={{ cursor: onItemClick ? 'pointer' : 'default' }}
              >
                {renderRow(it)}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

/** Small helper for the row-title / row-sub / row-aside layout. */
export function Row({ left, aside }: { left: React.ReactNode; aside?: React.ReactNode }) {
  return (
    <>
      <div style={{ flex: 1 }}>{left}</div>
      {aside && <div className="row-aside">{aside}</div>}
    </>
  );
}
